import React from 'react';
import { Link } from 'react-router-dom';
import './DataPrivacy.css';

const DataPrivacy = () => {
  return (
    <div className="data-privacy">
      <header className="header">
        <div className="container">
          <div className="nav-brand">
            <Link to="/" className="logo-link">
              <h1 className="logo">BNSN</h1>
            </Link>
          </div>
          <nav className="nav">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/contact" className="nav-link">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="main-content">
        <div className="container">
          <h1 className="page-title">Data Privacy</h1>
          <div className="content">
            <p className="last-updated">Last updated: December 2024</p>

            <section className="privacy-section">
              <h2>Data Collection and Processing</h2>
              <p>At BNSN, we are committed to protecting your personal data and ensuring your privacy. This page explains how we collect, use, and protect your information.</p>
              
              <h3>Types of Data We Collect</h3>
              <ul>
                <li><strong>Personal Information:</strong> Name, email address, phone number, and profile information</li>
                <li><strong>Account Data:</strong> Username, password (encrypted), and account preferences</li>
                <li><strong>Transaction Data:</strong> Listings, messages, and transaction history</li>
                <li><strong>Usage Data:</strong> How you interact with our platform, pages visited, and features used</li>
                <li><strong>Device Information:</strong> IP address, browser type, and device characteristics</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>How We Use Your Data</h2>
              <div className="usage-grid">
                <div className="usage-item">
                  <h3>🔧 Service Provision</h3>
                  <p>To provide and maintain our marketplace platform, process transactions, and facilitate communication between users.</p>
                </div>
                <div className="usage-item">
                  <h3>🔒 Security</h3>
                  <p>To protect against fraud, unauthorized access, and ensure the security of our platform and users.</p>
                </div>
                <div className="usage-item">
                  <h3>📈 Improvement</h3>
                  <p>To analyze usage patterns and improve our services, features, and user experience.</p>
                </div>
                <div className="usage-item">
                  <h3>📞 Communication</h3>
                  <p>To send important updates, notifications, and respond to your inquiries and support requests.</p>
                </div>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Data Security Measures</h2>
              <p>We implement comprehensive security measures to protect your data:</p>
              <ul>
                <li><strong>Encryption:</strong> All sensitive data is encrypted in transit and at rest</li>
                <li><strong>Access Controls:</strong> Strict access controls and authentication mechanisms</li>
                <li><strong>Regular Audits:</strong> Regular security audits and vulnerability assessments</li>
                <li><strong>Secure Infrastructure:</strong> Hosted on secure, monitored servers</li>
                <li><strong>Data Minimization:</strong> We only collect data that is necessary for our services</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>Your Privacy Rights</h2>
              <div className="rights-grid">
                <div className="right-item">
                  <h3>📋 Access</h3>
                  <p>Request a copy of your personal data we hold</p>
                </div>
                <div className="right-item">
                  <h3>✏️ Rectification</h3>
                  <p>Correct or update your personal information</p>
                </div>
                <div className="right-item">
                  <h3>🗑️ Erasure</h3>
                  <p>Request deletion of your data under certain circumstances</p>
                </div>
                <div className="right-item">
                  <h3>⏸️ Restriction</h3>
                  <p>Limit how we process your personal data</p>
                </div>
                <div className="right-item">
                  <h3>📤 Portability</h3>
                  <p>Receive your data in a structured, machine-readable format</p>
                </div>
                <div className="right-item">
                  <h3>🚫 Objection</h3>
                  <p>Object to certain types of data processing</p>
                </div>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Data Sharing and Third Parties</h2>
              <p>We do not sell your personal data. We may share your information only in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> Trusted third parties who help us operate our platform (hosting, analytics, customer support)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong>Consent:</strong> When you have given explicit consent for specific sharing</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>Data Retention</h2>
              <p>We retain your personal data only as long as necessary for the purposes outlined in this policy:</p>
              <ul>
                <li><strong>Account Data:</strong> Until you delete your account or request deletion</li>
                <li><strong>Transaction Data:</strong> As required by law and for legitimate business purposes</li>
                <li><strong>Usage Data:</strong> Typically for 2 years, unless longer retention is required</li>
                <li><strong>Marketing Data:</strong> Until you opt out or request deletion</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>International Data Transfers</h2>
              <p>Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data during such transfers, including:</p>
              <ul>
                <li>Standard contractual clauses approved by relevant authorities</li>
                <li>Adequacy decisions by data protection authorities</li>
                <li>Other appropriate safeguards as required by law</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>Children's Privacy</h2>
              <p>Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.</p>
            </section>

            <section className="privacy-section">
              <h2>Changes to This Policy</h2>
              <p>We may update this Data Privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.</p>
            </section>

            <section className="privacy-section">
              <h2>Contact Us About Data Privacy</h2>
              <p>If you have any questions about this Data Privacy policy or our data practices, please contact us:</p>
              <div className="contact-info">
                <p>📞 Phone: +91 9741906407</p>
                <p>📧 Email: bnsn.info7@gmail.com</p>
                <p>📋 Data Protection Officer: Available upon request</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2024 BNSN. All rights reserved.</p>
            <div className="footer-links">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-conditions">Terms & Conditions</Link>
              <Link to="/data-privacy">Data Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataPrivacy;
